import { editExpense, addExpense, deleteExpense } from './api.mjs';
import { renderModal, closeModal } from './ui.mjs';
import { formatCurrency, formatDate } from './utils.mjs';
import { addDeleteButtonToModal, appendModalFooterButton } from './uiHelpers.mjs';
import { createMonetaryInput, getCurrencyFractionDigits, splitNumber } from './monetaryInput.mjs';
import { createTextField } from './textInput.mjs';
import router from './router.mjs';
import { modalRegistry } from './modalRegistry.mjs';
import { openCurrencyPicker } from './currencyPicker.mjs';
import { createAddPersonRow } from './receiptHelpers.mjs';
import t from 'virtual:i18n';




/**
 * Opens a full-screen or modal "Split Expense" (Quick Split) view.
 * @param {Group} group 
 * @param {Function} [onSuccess] Callback when expense is successfully added/edited.
 * @param {Object} [expense] Existing expense object to edit (optional).
 */
export async function openSplitExpense(group, onSuccess, expense = null) {
    const modalAnchor = document.getElementById('modal-anchor') || document.body;

    // State
    let fabButton = null;
    let description = expense ? expense.description : '';
    let amountStr = expense ? expense.amount.toString() : '';
    let savedCurrency = null;
    try {
        savedCurrency = localStorage.getItem('splitpal:last_currency');
    } catch (e) {
        console.warn('Failed to read last_currency from localStorage', e);
    }
    let currency = expense ? expense.currency : (savedCurrency || group.currency || 'USD');
    let date = expense ? expense.date : new Date().toISOString().substring(0, 10);

    // Participants State: Local Copy
    let currentParticipants = [...(group.participants || [])];

    // Selected indices based on currentParticipants array
    let selectedParticipantIndices = [];

    // Track manual edits
    const touchedIndices = new Set();
    const cachedSplits = {}; // index -> number value
    const participantRows = []; // { el, checkbox, amountInput, nameSpan }

    let payers = [0];
    let payerAmounts = [0];

    if (expense) {
        // Edit Mode: Initialize from existing expense
        if (expense.participants) {
            selectedParticipantIndices = [...expense.participants];
        } else {
            selectedParticipantIndices = currentParticipants.map((_, i) => i);
        }

        if (expense.portions) {
            // Pre-fill cachedSplits from existing portions for display
            expense.participants.forEach((pIndex, i) => {
                cachedSplits[pIndex] = expense.portions[i];
                // If it's an uneven split, mark as touched so it's not immediately redistributed.
                // This ensures the values from the backend are preserved on initial render.
                if (expense.expenseType === 'UNEVEN') {
                    touchedIndices.add(pIndex);
                }
            });
        }

        if (expense.payers && expense.payers.length > 0) {
            const tempPayers = [...expense.payers];
            let tempAmounts = [...(expense.payerAmounts || [])];
            if (tempAmounts.length !== tempPayers.length) {
                tempAmounts = splitNumber(expense.amount, tempPayers.length, currency);
            }
            const combined = tempPayers.map((p, i) => ({ payer: p, amount: tempAmounts[i] || 0 }));
            combined.sort((a, b) => a.payer - b.payer);
            payers = combined.map(x => x.payer);
            payerAmounts = combined.map(x => x.amount);
        }
    } else {
        // Create Mode: Default all selected
        selectedParticipantIndices = currentParticipants.map((_, i) => i);
    }

    // Track initial values for dirty checking (edit mode only)
    const initialValues = expense ? {
        description: expense.description,
        amount: expense.amount,
        currency: expense.currency,
        date: expense.date,
        payers: JSON.stringify(payers),
        payerAmounts: JSON.stringify(payerAmounts),
        participants: JSON.stringify(selectedParticipantIndices.slice().sort()),
        portions: JSON.stringify(expense.portions || [])
    } : null;

    const container = document.createElement('div');
    Object.assign(container.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        fontFamily: "'Inter', sans-serif"
    });

    // --- Expense Name Field ---
    const nameDiv = document.createElement('div');
    nameDiv.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${t.expenseName}</label>`;

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.id = 'expense-name';
    nameInput.placeholder = t.forExampleGroceries;
    nameInput.value = description;
    nameInput.setAttribute('autocomplete', 'off');
    nameInput.setAttribute('autocorrect', 'off');
    nameInput.setAttribute('spellcheck', 'false');
    nameInput.setAttribute('data-form-type', 'other');
    Object.assign(nameInput.style, {
        width: '100%',
        padding: '12px',
        fontSize: '16px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: "'Inter', sans-serif"
    });
    nameInput.addEventListener('input', () => {
        description = nameInput.value;
        updateSplits();
    });
    nameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            amountInput.focus();
            amountInput.select();
        }
    });
    nameDiv.appendChild(nameInput);
    container.appendChild(nameDiv);

    // --- Amount & Currency Input ---
    const amountWrapper = document.createElement('div');
    amountWrapper.style.display = 'flex';
    amountWrapper.style.gap = '12px';

    const currencyDiv = document.createElement('div');
    currencyDiv.style.flex = '0 0 80px';

    const currencyLabel = document.createElement('label');
    currencyLabel.textContent = t.currency;
    currencyLabel.style.cssText = 'display:block; font-size:12px; color:#666; margin-bottom:4px;';

    const currencyBtn = document.createElement('div');
    currencyBtn.className = 'currency-select-btn';
    currencyBtn.title = 'Tap to change currency';
    currencyBtn.innerHTML = `
        <span class="currency-btn-code">${currency}</span>
        <span class="currency-chevron">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        </span>
    `;
    currencyBtn.addEventListener('click', () => {
        openCurrencyPicker(currency, (selectedCode) => {
            currency = selectedCode;
            currencyBtn.querySelector('.currency-btn-code').textContent = currency;
            if (!expense) {
                try {
                    localStorage.setItem('splitpal:last_currency', selectedCode);
                } catch (e) {
                    console.warn('Failed to save last_currency to localStorage', e);
                }
            }
            updateSplits();
        });
    });

    currencyDiv.appendChild(currencyLabel);
    currencyDiv.appendChild(currencyBtn);

    const amountDiv = document.createElement('div');
    amountDiv.style.flex = '1';
    amountDiv.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${t.amount}</label>`;

    const amountInput = createMonetaryInput({
        id: 'expense-amount',
        value: expense ? expense.amount : 0,
        placeholder: '0.00',
        currency: currency,
        className: ''
    });

    Object.assign(amountInput.style, {
        width: '100%',
        padding: '12px',
        fontSize: '16px',
        fontWeight: 'bold',
        border: '1px solid #ccc',
        borderRadius: '8px',
        outline: 'none',
        boxSizing: 'border-box'
    });

    amountInput.oninput = (e) => {
        amountStr = amountInput.getRawValue().toString();
        // Reset manual edits when total changes
        touchedIndices.clear();
        updateSplits();
    };
    amountDiv.appendChild(amountInput);

    amountWrapper.appendChild(currencyDiv);
    amountWrapper.appendChild(amountDiv);
    container.appendChild(amountWrapper);

    // --- Paid By Selector ---
    const payerWrapper = document.createElement('div');
    payerWrapper.style.marginTop = '8px';
    payerWrapper.style.display = 'flex';
    payerWrapper.style.alignItems = 'center';
    payerWrapper.style.justifyContent = 'space-between';

    const payerLabel = document.createElement('span');
    payerLabel.innerText = t.paidByText;
    payerLabel.style.fontSize = '14px';
    payerLabel.style.color = '#666';

    const payerBtn = document.createElement('button');
    Object.assign(payerBtn.style, {
        background: 'none',
        border: 'none',
        color: 'var(--color-primary)',
        fontWeight: '600',
        fontSize: '14px',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: '4px 0'
    });
    const updatePayerBtnText = () => {
        payers = payers.filter(idx => idx < currentParticipants.length);
        if (payers.length === 0) {
            payers = [0];
            payerAmounts = [parseFloat(amountStr) || 0];
        }

        if (payers.length === 1) {
            payerBtn.innerText = currentParticipants[payers[0]];
        } else {
            const summaries = payers.map((pIdx, i) => {
                const amt = payerAmounts[i] || 0;
                return `${currentParticipants[pIdx]} (${formatCurrency(amt, currency)})`;
            });
            payerBtn.innerText = summaries.join(', ');
        }
    };
    updatePayerBtnText();

    // Opens a bottom-sheet drawer to pick the payer
    const openPayerDrawer = async () => {
        const { openPayerSelectorDrawer } = await import('./payerSelector.mjs');
        openPayerSelectorDrawer({
            amount: parseFloat(amountStr) || 0,
            currency,
            participants: currentParticipants,
            payers,
            payerAmounts,
            onSave: (newPayers, newPayerAmounts, updatedParticipants) => {
                payers = newPayers;
                payerAmounts = newPayerAmounts;
                currentParticipants.splice(0, currentParticipants.length, ...updatedParticipants);
                updatePayerBtnText();
                updateSplits();
            }
        });
    };

    payerBtn.onclick = () => openPayerDrawer();

    payerWrapper.appendChild(payerLabel);
    payerWrapper.appendChild(payerBtn);
    container.appendChild(payerWrapper);


    // --- "Split between:" section header ---
    const splitBetweenHeader = document.createElement('div');
    splitBetweenHeader.textContent = t.splitBetween;
    Object.assign(splitBetweenHeader.style, {
        fontSize: '12px',
        color: '#666',
        marginTop: '8px',
        display: 'none',
    });
    container.appendChild(splitBetweenHeader);

    // --- Participants List ---
    const participantsList = document.createElement('div');
    participantsList.style.marginTop = '4px';

    function distribute() {
        const total = parseFloat(amountStr);
        if (isNaN(total) || total <= 0) return;

        // Cleanup touched indices for unselected participants
        // (If you unselect someone, their manual edit is discarded)
        const activeSelected = new Set(selectedParticipantIndices);
        for (let i of touchedIndices) {
            if (!activeSelected.has(i)) touchedIndices.delete(i);
        }

        // Calculate sum of locked (touched) amounts
        let touchedSum = 0;
        activeSelected.forEach(i => {
            if (touchedIndices.has(i)) {
                touchedSum += (cachedSplits[i] || 0);
            }
        });
        // Clamp to currency-correct decimal places (JPY=0, USD=2, KWD=3, …)
        const fractionDigits = getCurrencyFractionDigits(currency);
        touchedSum = parseFloat(touchedSum.toFixed(fractionDigits));

        const remainder = total - touchedSum;
        // Fix float drift
        const preciseRemainder = parseFloat(remainder.toFixed(fractionDigits));

        const untouchedIndices = selectedParticipantIndices.filter(i => !touchedIndices.has(i));

        if (untouchedIndices.length > 0) {
            // Distribute remainder among untouched
            // splitNumber returns array corresponding to 'parts' count
            const parts = splitNumber(preciseRemainder, untouchedIndices.length, currency);
            untouchedIndices.forEach((pIndex, i) => {
                cachedSplits[pIndex] = parts[i];
            });
        }
    }

    function syncDOMRows() {
        // 1. Clear if mismatched
        // To be safe and simple, if length differs, we rebuild.
        // Also if we have no rows yet.
        if (participantsList.children.length === 0 || participantRows.length !== currentParticipants.length) {
            participantsList.innerHTML = '';
            participantRows.length = 0;

            currentParticipants.forEach((name, index) => {
                const row = document.createElement('div');
                Object.assign(row.style, {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: '1px solid #f0f0f0',
                });

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.style.marginRight = '12px';
                checkbox.style.transform = 'scale(1.2)';
                checkbox.draggable = false;

                checkbox.onchange = (e) => {
                    const isChecked = e.target.checked;
                    if (isChecked) {
                        if (!selectedParticipantIndices.includes(index)) {
                            selectedParticipantIndices.push(index);
                        }
                    } else {
                        selectedParticipantIndices = selectedParticipantIndices.filter(i => i !== index);
                    }
                    updateSplits(); // Triggers distribute and updateUI
                };

                const nameSpan = document.createElement('span');
                nameSpan.innerText = name;
                nameSpan.style.flex = '1';
                nameSpan.style.fontSize = '16px';
                nameSpan.style.color = '#333';
                // Click on name toggles checkbox
                nameSpan.onclick = () => {
                    checkbox.click();
                };
                nameSpan.style.cursor = 'pointer';

                const amountInput = createMonetaryInput({
                    id: `participant-amount-${index}`,
                    value: 0,
                    placeholder: '0.00',
                    currency: currency,
                    className: ''
                });

                Object.assign(amountInput.style, {
                    width: '100px',
                    padding: '8px',
                    textAlign: 'right',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontWeight: 'bold'
                });

                amountInput.addEventListener('input', (e) => {
                    const val = amountInput.getRawValue();
                    if (!isNaN(val) && val >= 0) {
                        cachedSplits[index] = val;
                        touchedIndices.add(index);
                        updateSplits();
                    }
                });

                // Helper text for excluded
                const exclSpan = document.createElement('span');
                exclSpan.innerText = 'Excl.';
                exclSpan.style.color = '#ccc';
                exclSpan.style.fontSize = '12px';
                exclSpan.style.display = 'none';

                row.appendChild(checkbox);
                row.appendChild(nameSpan);
                row.appendChild(amountInput);
                row.appendChild(exclSpan);
                participantsList.appendChild(row);

                participantRows.push({ row, checkbox, amountInput, exclSpan });
            });

            // Add Participant Button Row
            const addRow = createAddPersonRow((newName) => {
                if (newName && newName.trim()) {
                    currentParticipants.push(newName.trim());
                    // Auto-select the new participant
                    selectedParticipantIndices.push(currentParticipants.length - 1);
                    updateSplits();
                    // Update payer button to ensure cycles cover new person
                    updatePayerBtnText();
                }
            }, t.addParticipant);
            // Adjust styling to match split expense form padding
            addRow.style.marginTop = '8px';
            addRow.style.paddingLeft = '12px';
            participantsList.appendChild(addRow);
        }
    }

    function renderParticipants() {
        syncDOMRows(); // Ensure elements exist

        distribute(); // Calculate values

        const amountVal = parseFloat(amountStr);
        const isValidAmount = !isNaN(amountVal) && amountVal > 0;

        participantRows.forEach((item, index) => {
            const { checkbox, amountInput, exclSpan } = item;

            // 1. Update Checkbox
            checkbox.checked = selectedParticipantIndices.includes(index);

            // 2. Update Input Visibility and Value
            if (selectedParticipantIndices.includes(index)) {
                amountInput.style.display = 'block';
                exclSpan.style.display = 'none';

                if (isValidAmount) {
                    // Check if this input is focused. If so, don't touch value (interferes with typing)
                    // UNLESS the value is undefined in cache (shouldn't happen if initialized)
                    const val = cachedSplits[index] ?? 0;
                    if (document.activeElement !== amountInput) {
                        // setMonetaryValue formats with the correct decimal places for the currency
                        amountInput.setMonetaryValue(val);
                    }

                    // Visual cue for manually edited items?
                    amountInput.style.borderColor = touchedIndices.has(index) ? 'var(--color-primary)' : '#ddd';
                } else {
                    amountInput.value = '';
                }

            } else {
                amountInput.style.display = 'none';
                exclSpan.style.display = 'block';
            }
        });
    }

    function updateSplits() {
        const val = parseFloat(amountStr);
        const hasAmount = !isNaN(val) && val > 0;
        const hasParticipants = currentParticipants.length > 0;

        // For a single payer, automatically sync their payment amount to the total
        if (payers.length === 1) {
            payerAmounts = [hasAmount ? val : 0];
        } else if (payers.length === 0) {
            payers = [0];
            payerAmounts = [hasAmount ? val : 0];
        }

        // Toggle visibility based on amount and participants
        payerWrapper.style.display = (hasAmount && hasParticipants) ? 'flex' : 'none';
        splitBetweenHeader.style.display = (hasAmount && hasParticipants) ? 'block' : 'none';
        participantsList.style.display = hasAmount ? 'block' : 'none';

        const promo = document.getElementById('receipt-promo-banner');
        if (promo) {
            promo.style.display = hasAmount ? 'none' : 'flex';
        }

        let isValid = hasAmount && description.trim().length > 0;

        if (hasAmount) {
            renderParticipants();

            // Validation Logic
            let currentSum = 0;
            let hasNegative = false;

            selectedParticipantIndices.forEach(idx => {
                const v = cachedSplits[idx] || 0;
                currentSum += v;
                if (v < 0) hasNegative = true;
            });

            // Tolerance check (approx equality)
            if (Math.abs(currentSum - val) > 0.05) isValid = false;

            // Payer sum check
            let currentPayerSum = 0;
            payerAmounts.forEach(v => {
                currentPayerSum += v;
            });
            const fractionDigits = getCurrencyFractionDigits(currency);
            currentPayerSum = parseFloat(currentPayerSum.toFixed(fractionDigits));
            if (Math.abs(currentPayerSum - val) > 0.05) isValid = false;

            // Negative check
            if (hasNegative) isValid = false;

            // At least one participant check
            if (selectedParticipantIndices.length === 0) isValid = false;
        } else {
            isValid = false;
        }

        // Check if expense has been modified (edit mode only)
        let isDirty = true; // Always true for create mode
        if (expense && initialValues) {
            // Check each field for changes
            const currentAmount = parseFloat(amountStr);
            const currentParticipants = JSON.stringify(selectedParticipantIndices.slice().sort());

            // Build current portions for comparison
            const currentPortions = [];
            selectedParticipantIndices.forEach(idx => {
                currentPortions.push(cachedSplits[idx] || 0);
            });
            const currentPortionsStr = JSON.stringify(currentPortions);

            const currentPayersStr = JSON.stringify(payers);
            const currentPayerAmountsStr = JSON.stringify(payerAmounts);

            isDirty = (
                description !== initialValues.description ||
                currentAmount !== initialValues.amount ||
                currency !== initialValues.currency ||
                date !== initialValues.date ||
                currentPayersStr !== initialValues.payers ||
                currentPayerAmountsStr !== initialValues.payerAmounts ||
                currentParticipants !== initialValues.participants ||
                currentPortionsStr !== initialValues.portions
            );
        }

        if (fabButton) {
            const shouldEnable = isValid && isDirty;
            const headerSection = fabButton.parentElement;
            const deleteBtn = headerSection ? headerSection.querySelector('.delete-expense-btn') : null;
            const backBtn = headerSection ? headerSection.querySelector('.sheet-back-btn') : null;
            const modalTitle = headerSection ? headerSection.querySelector('.modal-title') : null;
            const cancelBtn = headerSection ? headerSection.querySelector('.cancel-edit-btn') : null;

            if (expense) {
                // Edit mode
                if (isDirty) {
                    fabButton.style.display = 'flex';
                    fabButton.disabled = !isValid;
                    fabButton.style.opacity = isValid ? '1' : '0.5';
                    fabButton.style.cursor = isValid ? 'pointer' : 'not-allowed';
                    if (deleteBtn) deleteBtn.style.display = 'none';
                    if (backBtn) backBtn.style.display = 'none';
                    if (cancelBtn) cancelBtn.style.display = 'flex';
                } else {
                    fabButton.style.display = 'none';
                    if (deleteBtn) deleteBtn.style.display = 'flex';
                    if (backBtn) backBtn.style.display = '';
                    if (cancelBtn) cancelBtn.style.display = 'none';
                }
            } else {
                // Create mode
                fabButton.style.display = 'flex';
                fabButton.disabled = !shouldEnable;
                fabButton.style.opacity = shouldEnable ? '1' : '0.5';
                fabButton.style.cursor = shouldEnable ? 'pointer' : 'not-allowed';
            }
        }
    }

    updateSplits(); // Initial render
    container.appendChild(participantsList);

    // --- Receipt Promo & Scanning ---
    const scanCountStr = localStorage.getItem('splitpal:scanCount');
    let scanCount = 0;
    if (scanCountStr) {
        scanCount = parseInt(scanCountStr, 10);
    } else if (localStorage.getItem('splitpal:hasScannedAnonymous') === 'true') {
        scanCount = 1;
    }
    const hasReachedLimit = scanCount >= 3;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    container.appendChild(fileInput);

    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.capture = 'environment';
    cameraInput.style.display = 'none';
    container.appendChild(cameraInput);

    const handleFileSelected = async (file) => {
        if (!file) return;

        try {
            const { createExpenseFromReceipt } = await import('./api.mjs');
            const { showSpinner, hideSpinner, updateSpinnerText, compressImageIfNeeded } = await import('./utils.mjs');
            showSpinner(t.uploadingImage);

            // Compress image client-side if needed
            const processedFile = await compressImageIfNeeded(file);

            // Convert file to Base64
            const base64Data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result;
                    const base64 = result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = () => reject(new Error('Failed to read image file'));
                reader.readAsDataURL(processedFile);
            });

            updateSpinnerText(t.analyzingReceipt);

            // Call API with base64 payload directly
            const currentDescription = nameInput.value.trim();
            const updatedGroup = await createExpenseFromReceipt(group.id, null, currentDescription, base64Data);

            scanCount += 1;
            localStorage.setItem('splitpal:scanCount', scanCount.toString());
            localStorage.setItem('splitpal:hasScannedAnonymous', 'true');

            if (onSuccess) onSuccess();
            closeModal(modal); // modal is hoisted, it will be defined

            // If expense was created, navigate to the newly created expense
            if (updatedGroup && updatedGroup.expenses && updatedGroup.expenses.length > 0) {
                const { cache } = await import('./main.mjs');
                cache.group = updatedGroup;

                const newExpense = updatedGroup.expenses[0];
                router.navigate(`/home/${group.id}/receipt/${newExpense.id}`);
            } else {
                router.navigate(`/home/${group.id}`);
            }

        } catch (err) {
            console.error(err);
            alert('Failed to process receipt: ' + err.message);
        } finally {
            const { hideSpinner } = await import('./utils.mjs');
            hideSpinner();
        }
    };

    fileInput.addEventListener('change', (e) => handleFileSelected(e.target.files[0]));
    cameraInput.addEventListener('change', (e) => handleFileSelected(e.target.files[0]));

    const promoWrapper = document.createElement('div');
    promoWrapper.id = 'receipt-promo-banner';
    const initHasAmount = !isNaN(parseFloat(amountStr)) && parseFloat(amountStr) > 0;
    Object.assign(promoWrapper.style, {
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        display: initHasAmount ? 'none' : 'flex',
        alignItems: 'center',
        gap: '12px',
        cursor: 'pointer',
        border: '1px dashed #e0e0e0', // Light dashed border
        transition: 'all 0.2s ease',
    });

    // Receipt Icon (Left)
    const promoIconWrap = document.createElement('div');
    promoIconWrap.style.display = 'flex';
    promoIconWrap.style.alignItems = 'center';
    promoIconWrap.style.justifyContent = 'center';
    promoIconWrap.style.color = '#757575';
    promoIconWrap.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z"/>
          <path d="M16 8h-8"/>
          <path d="M16 12h-8"/>
          <path d="M10 16h-2"/>
        </svg>
    `;

    const promoTextWrap = document.createElement('div');
    promoTextWrap.style.flex = '1';
    promoTextWrap.innerHTML = hasReachedLimit ? `
        <div style="font-size:14px; color:#444; font-weight:600; margin-bottom:1px;">${t.needMoreScansGetApp}</div>
        <div style="font-size:12px; color:#757575; font-weight:400;">${t.getSplitPalApp}</div>
    ` : `
        <div style="font-size:14px; color:#444; font-weight:600; margin-bottom:1px;">${t.wantToSplitInReceipt}</div>
        <div style="font-size:12px; color:#999; font-weight:400;">${t.tapToScan}</div>
    `;

    // Small Chevron (Right)
    const chevronWrap = document.createElement('div');
    chevronWrap.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
    `;

    promoWrapper.appendChild(promoIconWrap);
    promoWrapper.appendChild(promoTextWrap);
    promoWrapper.appendChild(chevronWrap);

    promoWrapper.addEventListener('mouseenter', () => {
        promoWrapper.style.backgroundColor = '#fcfcfc';
        promoWrapper.style.borderColor = '#ccc';
        chevronWrap.querySelector('svg').style.stroke = '#999';
    });
    promoWrapper.addEventListener('mouseleave', () => {
        promoWrapper.style.backgroundColor = '#fff';
        promoWrapper.style.borderColor = '#e0e0e0';
        chevronWrap.querySelector('svg').style.stroke = '#ccc';
    });

    promoWrapper.addEventListener('click', async () => {
        if (!hasReachedLimit) {
            const { trackEvent } = await import('./utils.mjs');
            trackEvent('scan_splitpal_anonymous');

            const { renderModal, closeModal: closeOptModal } = await import('./ui.mjs');
            const modalAnchor = document.getElementById('modal-anchor') || document.body;

            const optionsContainer = document.createElement('div');
            optionsContainer.style.display = 'flex';
            optionsContainer.style.flexDirection = 'column';
            optionsContainer.style.gap = '12px';
            optionsContainer.style.padding = '8px 0';

            const cameraBtn = document.createElement('button');
            cameraBtn.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 12px;">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                </svg>
                ${t.takePhoto}
            `;
            Object.assign(cameraBtn.style, {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1D1B20',
                cursor: 'pointer'
            });
            cameraBtn.onclick = () => {
                if (optModal) closeOptModal(optModal);
                cameraInput.click();
            };

            const libraryBtn = document.createElement('button');
            libraryBtn.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 12px;">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                ${t.chooseFromLibrary}
            `;
            Object.assign(libraryBtn.style, {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1D1B20',
                cursor: 'pointer'
            });
            libraryBtn.onclick = () => {
                if (optModal) closeOptModal(optModal);
                fileInput.click();
            };

            optionsContainer.appendChild(cameraBtn);
            optionsContainer.appendChild(libraryBtn);

            const optModal = renderModal({
                anchorEl: modalAnchor,
                title: t.chooseImageSource,
                body: optionsContainer,
                buttons: []
            });
            return;
        }

        const { trackEvent } = await import('./utils.mjs');
        trackEvent('scan_splitpal_download');

        const { isCreator, startCreatorHandoff } = await import('./creatorHandoff.mjs');
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const { STORE_LINKS: storeLinks } = await import('./config.mjs');
        if (isCreator()) {
            startCreatorHandoff();
        } else {
            const targetUrl = isIOS ? storeLinks.ios : storeLinks.android;
            window.open(targetUrl, '_blank');
        }
    });

    container.appendChild(promoWrapper);


    // --- Save Logic ---
    const handleSave = async () => {
        const amount = parseFloat(amountStr);
        if (!description.trim()) {
            alert('Please enter a description');
            return;
        }
        if (isNaN(amount) || amount <= 0) {
            alert('Please enter a valid amount');
            return;
        }
        if (selectedParticipantIndices.length === 0) {
            alert('Please select at least one person to split with');
            return;
        }

        // Final Splits
        selectedParticipantIndices.sort((a, b) => a - b);

        // Use cached splits for final result
        const finalSplits = [];
        let validationSum = 0;
        selectedParticipantIndices.forEach(idx => {
            const val = cachedSplits[idx] || 0;
            finalSplits.push(val);
            validationSum += val;
        });

        // Check for sum mismatch (float tolerance)
        if (Math.abs(validationSum - amount) > 0.05) {
            // If total doesn't match, warn user
            alert(`The sum of splits (${formatCurrency(validationSum, currency)}) does not match the total amount (${formatCurrency(amount, currency)}). Please adjust.`);
            return;
        }

        // Construct input exactly as backend expects
        const input = {
            expenseType: 'UNEVEN',
            description: description.trim(),
            currency: currency,
            date: date,
            participants: selectedParticipantIndices,
            portions: finalSplits,
            payers: payers,
            payerAmounts: payerAmounts,
            itemizedData: null
        };

        try {
            if (expense) {
                // Edit Mode
                await editExpense(group.id, expense.id, input, currentParticipants);
            } else {
                // Create Mode
                // addExpense needs the full updated list of participant names 
                // so backend can add any new names to the Group document.
                await addExpense(group.id, input, currentParticipants);
                const { trackEvent } = await import('./utils.mjs');
                trackEvent('expense_created', { currency: currency });
            }

            if (onSuccess) onSuccess();

            // Close modal and navigate back to group home
            closeModal(modal);
            router.navigate(`/home/${group.id}`);
        } catch (e) {
            console.error(e);
            alert('Failed to save expense: ' + e.message);
        }
    };

    const modal = renderModal({
        anchorEl: modalAnchor,
        title: expense ? t.editExpense : t.addNewExpense,
        body: container,
        buttons: [], // No default buttons
        onClose: () => {
            // De-register live-update callback
            modalRegistry.onGroupUpdate = null;
            // Replace the expense URL with the group URL (don't push to history)
            router.navigate(`/home/${group.id}`, true);
        }
    });

    // Set modal ID for cleanup when navigating
    modal.id = 'split-expense-modal';

    // Register live-update callback.
    // Only syncs participant names when the user hasn't started editing (isDirty = false).
    modalRegistry.onGroupUpdate = (updatedGroup) => {
        const isCurrentlyDirty = expense && initialValues ? (
            description !== initialValues.description ||
            parseFloat(amountStr) !== initialValues.amount ||
            date !== initialValues.date ||
            JSON.stringify(payers) !== initialValues.payers ||
            JSON.stringify(payerAmounts) !== initialValues.payerAmounts
        ) : amountStr !== '';

        if (!isCurrentlyDirty) {
            // Safe to sync participant list (e.g., someone was renamed)
            currentParticipants.splice(0, currentParticipants.length, ...updatedGroup.participants);
            updateSplits();
        }
        // Always keep payer button text in sync with latest names
        updatePayerBtnText();
    };

    // Pull modal higher for better visibility (especially when creating new expense)
    const modalSheetContent = modal.querySelector('.bottom-sheet-content');
    if (modalSheetContent) {
        modalSheetContent.style.minHeight = '90dvh';
    }




    // Add delete button to header (only for edit mode)
    if (expense) {
        const confirmMessage = `Are you sure you want to delete "${expense.description}"? This action cannot be undone.`;

        addDeleteButtonToModal(
            modal,
            async () => {
                try {
                    // Call delete API
                    await deleteExpense(group.id, expense.id);

                    // Close modal
                    closeModal(modal);

                    // Navigate back to group home
                    router.navigate(`/home/${group.id}`);
                } catch (error) {
                    console.error('Failed to delete expense:', error);
                    alert('Failed to delete expense. Please try again.');
                }
            },
            confirmMessage
        );
    }


    // Auto-focus: name field for new expenses only (desktop only)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
        setTimeout(() => {
            if (!expense) {
                nameInput.focus();
            }
        }, 350); // Slightly after modal animation completes (300ms)
    }

    // --- Action Button in Header ---
    fabButton = document.createElement('button');
    const actionText = expense ? t.update : t.create;
    fabButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span>${actionText}</span>`;
    fabButton.className = 'header-action-text-btn';
    fabButton.onclick = handleSave;

    // Add to header section
    const headerSection = modal.querySelector('.sheet-header-section');
    if (headerSection) {
        if (expense) {
            const cancelBtn = document.createElement('button');
            cancelBtn.className = 'header-action-text-btn cancel-edit-btn';
            cancelBtn.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
            cancelBtn.style.color = '#333';
            cancelBtn.style.display = 'none';
            cancelBtn.style.marginLeft = '-8px';
            cancelBtn.style.marginRight = '8px';
            cancelBtn.onclick = (e) => {
                e.stopPropagation();
                if (!expense || !initialValues) return;

                // Revert changes
                description = initialValues.description;
                nameInput.value = description;

                amountStr = initialValues.amount.toString();
                amountInput.setMonetaryValue(initialValues.amount);

                currency = initialValues.currency;
                const currencyBtnCode = currencyBtn.querySelector('.currency-btn-code');
                if (currencyBtnCode) currencyBtnCode.textContent = currency;

                date = initialValues.date;

                payers = JSON.parse(initialValues.payers);
                payerAmounts = JSON.parse(initialValues.payerAmounts);
                updatePayerBtnText();

                selectedParticipantIndices = JSON.parse(initialValues.participants);

                const initialPortions = JSON.parse(initialValues.portions);
                touchedIndices.clear();
                expense.participants.forEach((pIndex, i) => {
                    cachedSplits[pIndex] = initialPortions[i];
                    if (expense.expenseType === 'UNEVEN') {
                        touchedIndices.add(pIndex);
                    }
                });

                updateSplits();
            };
            headerSection.insertBefore(cancelBtn, headerSection.firstChild);
        }
        headerSection.appendChild(fabButton);
    }

    // Initial validation check
    updateSplits();
}
